import { useEffect, useMemo, useRef, useState } from "react";
import { fmtSecs, imeComposing } from "../util";
import { useHold, useKeepPlace, usePlaceBefore } from "../fresh";
import { api, ApiError } from "../api/client";
import type { AIAsset, Message } from "../api/types";
import { followNext, quoteText, quoteWho } from "./Transcript";
import { needsFit } from "../videoFit";
import { sizeText, useVideoFit, whatFailed } from "../useVideoFit";
import { previewURL } from "./MediaGallery";
import { Glyph } from "./MediaIcon";
import { ShellIcon } from "./ShellIcons";

interface Props {
  waID: string;
  /** Re-fetch the lead so the new message appears in the transcript. */
  onSent: () => void;
  /** Whether this operator may send a written reply at all. */
  canText: boolean;
  /** Whether this operator may send a voice note. */
  canVoice: boolean;
  /** 回复: the line the next message quotes, and how to let it go. */
  replyTo?: Message | null;
  onClearReply?: () => void;
  /** The customer's name, for a quote of their line. */
  peerName?: string;
  /**
   * Who answers this customer by themself right now (小爱, 剧本), for the line over the box; absent when
   * nobody does. Anything sent from here hands the chat to the sender (api console pauseForReply).
   */
  botName?: string;
  /** Words put in the box from outside (价钱's 「按价钱就放进输入框」): added after what is typed, a new seq each time. */
  fill?: { text: string; seq: number } | null;
}

/** The synthesiser's cap. Longer than this cannot be spoken, only written. */
const VOICE_MAX = 400;
/** WhatsApp's own cap for a text body, matched so the count runs out first. */
const TEXT_MAX = 4096;

/** The ones the agents actually type, in the order they reach for them. */
const EMOJI = [
  "😊", "😁", "🥰", "🙏", "❤️", "💜", "👍", "👌",
  "🔥", "✨", "🌙", "😴", "💤", "😉", "😅", "🤗",
  "📦", "🎁", "✅", "⚠️", "👇", "👋", "💰", "🚚",
];

/**
 * 素材库's pictures and clips that may still go out, all of them, in its own
 * order: the published 小爱 profile's files (the same list the 素材库 tab shows),
 * fetched once per page and searched here. It offered the first eight only, and
 * a file further down could not be sent at all (owner, 2026-10-25: 「不能搜素材库
 * 里的素材发？全是写死的？」). Nothing when this colleague may not read 小爱's page
 * — the paperclip then offers the computer's files only, as before.
 */
let library: Promise<{ id: number; assets: AIAsset[] } | null> | null = null;
function loadLibrary() {
  library ??= api.ai().then(
    (ov) => {
      const p = ov.published;
      if (!p) return null;
      const today = Date.now();
      const assets = [...p.assets]
        .filter((a) => /^(image|video)\//.test(a.mime_type) && !(a.valid_until && Date.parse(a.valid_until) < today))
        .sort((a, b) => a.ord - b.ord);
      return assets.length ? { id: p.id, assets } : null;
    },
    () => {
      // Not this colleague's, or the line was down: asked again next time.
      library = null;
      return null;
    },
  );
  return library;
}

/** Whether a file answers what was typed in the card's search: every word in its name, key or rule. */
function libMatch(a: AIAsset, q: string): boolean {
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = `${a.label} ${a.key} ${a.when_to_send}`.toLowerCase();
  return words.every((w) => hay.includes(w));
}

/**
 * A 素材库 file as a tile: its small preview (shared with 剧本's tiles), its name, ▶ on a clip. The
 * preview is asked for once the tile scrolls near the card's view, so a library of a hundred files
 * opens without a hundred requests.
 */
function LibTile({ id, asset, busy, onPick }: { id: number; asset: AIAsset; busy: boolean; onPick: () => void }) {
  const [thumb, setThumb] = useState<string | null>(null);
  const [near, setNear] = useState(false);
  const ref = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || near) return;
    if (typeof IntersectionObserver === "undefined") {
      setNear(true);
      return;
    }
    const io = new IntersectionObserver(
      (es) => {
        if (es.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { root: el.closest(".wc-att-g"), rootMargin: "120px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near]);
  useEffect(() => {
    if (!near) return;
    let live = true;
    previewURL(`a:${id}:${asset.key}:${asset.object_key}:160`, () => api.aiAssetPreview(id, asset.key, asset.object_key, 160)).then(
      (u) => live && setThumb(u),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [near, id, asset.key, asset.object_key]);
  return (
    <button ref={ref} type="button" onClick={onPick} title={asset.label || asset.key} disabled={busy}>
      <span className="wc-att-th">
        {thumb ? <img src={thumb} alt="" /> : <i className="wc-spin" aria-hidden="true" />}
        {asset.mime_type.startsWith("video/") && (
          <b>
            <ShellIcon name="play" size={9} />
          </b>
        )}
      </span>
      <span>{asset.label || asset.key}</span>
    </button>
  );
}

/**
 * A finger, not a mouse: the phone's own keyboard has Enter for a new line and
 * its own emoji, as in the WhatsApp app.
 */
const touch = () => window.matchMedia("(pointer: coarse)").matches;

/**
 * Keeps the typing box focused through a press, so the phone's keyboard stays
 * up after 发送 the way WhatsApp's does. A press that moved the focus to the
 * button took the keyboard down with every message.
 */
const keepFocus = (e: React.PointerEvent) => e.preventDefault();

/** How loud the microphone is, as a bar's height (18–100 %), from one frame of the waveform. */
function level(buf: Uint8Array): number {
  let sum = 0;
  for (const v of buf) {
    const x = (v - 128) / 128;
    sum += x * x;
  }
  return Math.round(18 + 82 * Math.min(1, Math.sqrt(sum / Math.max(1, buf.length)) * 4));
}

/** The live bars kept while recording: the newest 44 fit the bar (prototype v2.20 chat-rec-w). */
const REC_BARS = 44;

/**
 * WhatsApp's composer, as prototype v2.20 draws it: one rounded box with the
 * emoji, the words, the paperclip and 转语音, and one round button beside it —
 * the mic while the box is empty, send once something is in it. Every other
 * control that was in the box (listen, speak, mic, emoji) squeezed the words
 * down to four characters a line on a phone (owner, 2026-10-04).
 *
 * 转语音 speaks the typed words in the cloned voice. It always plays first —
 * a strip over the box with the clip and 发语音 — because a voice note cannot be
 * taken back, and the voice can say a word wrong. Enter sends TEXT, never
 * voice, and only from a keyboard: on a phone Enter is a new line and the
 * button sends, as in the app. With a picture attached the send button sends
 * the picture and the box is its caption. A picture copied anywhere — 「复制图片」
 * in Chrome, a screenshot — pastes into the box as that attachment, as it does
 * in WhatsApp (owner 10/13: 「到chrome找图然后copy了直接贴到whatsapp发送 …我也要这个功能」).
 * The paperclip opens a card first: 照片和影片 from the computer, and 素材库's
 * files, each put over the box to be seen before it goes (QA G2-13).
 *
 * The rules about what may not be spoken (links, account numbers, the standard
 * safety wording) live on the server, not here: this console is deployed
 * separately and can be older than the rule it would be enforcing.
 */
export default function VoiceComposer({ waID, onSent, canText, canVoice, replyTo, onClearReply, peerName = "", botName, fill }: Props) {
  // Typed words survive 刷新 on the new-version line (fresh.tsx), the one
  // reload this console makes that does not wait for them.
  const textBefore = usePlaceBefore(`reply:${waID}`);
  const [text, setText] = useState(textBefore ?? "");
  useKeepPlace(`reply:${waID}`, text.trim() ? text : null);
  const [busy, setBusy] = useState<"" | "voice" | "text">("");
  const [err, setErr] = useState<string | null>(null);
  // 转语音: the clip of the words, and whether one is being made. The box stays
  // open while it is: typing on, or ✕, drops the one on its way (ttsSeq).
  const [clip, setClip] = useState<string | null>(null);
  const [making, setMaking] = useState(false);
  const ttsSeq = useRef(0);
  const [attach, setAttach] = useState<File | null>(null);
  const [attachURL, setAttachURL] = useState<string | null>(null);
  // Where the picture over the box came from: the computer, or 素材库 (said under its name).
  const [attachLib, setAttachLib] = useState(false);
  // A clip too big for WhatsApp, or not an MP4, is made small enough here
  // before it goes (videoFit.ts); fitNote says what it was made from.
  const { fit, line: fitLine, fitting, stop: stopFit } = useVideoFit();
  const [fitNote, setFitNote] = useState("");
  // The card over the box: the emoji, or the paperclip's 照片和影片 and 素材库.
  const [pop, setPop] = useState<"" | "emoji" | "attach">("");
  const [lib, setLib] = useState<{ id: number; assets: AIAsset[] } | null>(null);
  // Whether 素材库 has been read (it may have nothing for this colleague), and what is searched in it.
  const [libRead, setLibRead] = useState(false);
  const [libQ, setLibQ] = useState("");
  // New words search from the first file: the grid was left scrolled where it was, the first matches hidden above (QA R4-04).
  const libGrid = useRef<HTMLDivElement>(null);
  const searchLib = (q: string) => {
    setLibQ(q);
    if (libGrid.current) libGrid.current.scrollTop = 0;
  };
  // The 素材库 file being fetched to go over the box.
  const [libBusy, setLibBusy] = useState("");
  // Recording the agent's own voice, as WhatsApp does it: press the mic, a
  // timer runs; send at once, or stop to listen first; the bin throws it away.
  // Kept apart from 转语音 — that one reads typed words, this is the colleague.
  const [rec, setRec] = useState<"off" | "on" | "ready">("off");
  const [secs, setSecs] = useState(0);
  const [take, setTake] = useState<{ blob: Blob; url: string } | null>(null);
  // The voice coming in, as bars from the right (prototype v2.20): how loud, every quarter second.
  const [levels, setLevels] = useState<{ n: number; bars: number[] }>({ n: 0, bars: [] });
  const meterRef = useRef<{ ctx: AudioContext; timer: number } | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const tickRef = useRef<number | null>(null);
  // Send pressed while still recording: the take goes out as soon as it is whole.
  const sendOnStop = useRef(false);
  // The bin pressed while recording. The recorder hands over its last piece
  // and stops a moment later, and that used to bring the binned take back.
  const binned = useRef(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const boxRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    if (!fill) return;
    setText((t) => (t.trim() ? `${t.trimEnd()}\n` : "") + fill.text);
    boxRef.current?.focus({ preventScroll: true });
  }, [fill]);
  const footRef = useRef<HTMLDivElement | null>(null);

  const empty = text.trim() === "";
  const overText = text.length > TEXT_MAX;
  const overVoice = text.length > VOICE_MAX;
  const working = busy !== "";
  const canSendText = canText && !working && !fitting && (attach ? !overText : !empty && !overText);
  // Too long to speak is not too long to write, so the two disable on different
  // limits rather than the box refusing everything at 400.
  const canSpeak = canVoice && !working && !empty && !overVoice && !attach && !fitting;
  // The count is noise until it matters. Which limit it counts against depends
  // on what is still possible: past 400 the question stops being "will this fit
  // in a voice note" and becomes "will WhatsApp take it".
  const nearVoice = canVoice && text.length > VOICE_MAX * 0.8 && !overVoice;
  const counter = overVoice && canVoice ? `${text.length}/${TEXT_MAX}` : nearVoice ? `${text.length}/${VOICE_MAX}` : overText ? `${text.length}/${TEXT_MAX}` : "";
  // Anything not yet sent keeps the tab from reloading itself for a new build
  // (fresh.tsx): typed words, a picture, a recording, a send on its way.
  useHold(!empty || !!attach || rec !== "off" || working || fitting);

  // Answering a line: the box is where the words go next, as WhatsApp puts
  // the cursor there.
  const quoting = replyTo?.ref ?? "";
  useEffect(() => {
    if (quoting) boxRef.current?.focus();
  }, [quoting]);

  // The box grows with the message, up to a few lines, like WhatsApp's. Empty, it is the one line rows={1} gives it,
  // with nothing to measure: measuring it as it came laid the whole of 对话 out on the spot, off screen behind 今天,
  // as the console opened (0.3 s, 10/27). A picture's caption box is measured as before, for its longer placeholder.
  const captioning = !!attach;
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    if (!text && !captioning) {
      el.style.height = "";
      return;
    }
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  }, [text, captioning]);

  // Object URLs are not garbage collected on their own — without this, an agent
  // working a shift leaks one buffer per preview.
  useEffect(
    () => () => {
      if (clip) URL.revokeObjectURL(clip);
    },
    [clip],
  );

  // Leaving the conversation must not leave the microphone open or a
  // recording in memory.
  useEffect(
    () => () => {
      stopTicking();
      releaseMic();
    },
    [],
  );

  // A card over the box closes with a press anywhere else, or Esc (prototype v2.20 chatOutside).
  useEffect(() => {
    if (!pop) return;
    const away = (e: PointerEvent) => {
      if (!footRef.current?.contains(e.target as Node)) setPop("");
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPop("");
    };
    document.addEventListener("pointerdown", away);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", away);
      window.removeEventListener("keydown", onKey);
    };
  }, [pop]);

  // The paperclip's 素材库, read the first time the card opens; its search starts empty each time.
  useEffect(() => {
    if (pop !== "attach") {
      setLibQ("");
      return;
    }
    if (lib) return;
    let live = true;
    void loadLibrary().then((l) => {
      if (!live) return;
      setLib(l);
      setLibRead(true);
    });
    return () => {
      live = false;
    };
  }, [pop, lib]);
  const libShown = useMemo(() => (lib ? lib.assets.filter((a) => libMatch(a, libQ)) : []), [lib, libQ]);

  function pickAttach(f: File | null, fromLib = false) {
    setAttachURL((old) => {
      if (old) URL.revokeObjectURL(old);
      return f ? URL.createObjectURL(f) : null;
    });
    setAttach(f);
    setAttachLib(!!f && fromLib);
    setFitNote("");
    setErr(null);
  }

  // A picked file: a clip that needs it is made small enough first.
  async function pickFile(f: File, fromLib = false) {
    setPop("");
    dropClip();
    if (!needsFit(f)) {
      pickAttach(f, fromLib);
      return;
    }
    pickAttach(null);
    try {
      const out = await fit(f);
      pickAttach(out, fromLib);
      setFitNote(`已压缩：${sizeText(f.size)} → ${sizeText(out.size)}`);
    } catch (e) {
      const why = whatFailed(e);
      if (why) setErr(why);
    }
  }

  // 素材库's file over the box, as the computer's would be: fetched, made sendable, seen before it goes.
  // It goes to the customer the way a picked file does (sendMedia); nothing new is asked of the server.
  async function pickLibrary(a: AIAsset) {
    if (!lib || libBusy) return;
    setLibBusy(a.key);
    setErr(null);
    try {
      const blob = await api.aiAsset(lib.id, a.key);
      const type = blob.type || a.mime_type;
      const ext = (a.object_key.split(".").pop() || type.split("/")[1] || "jpg").toLowerCase();
      const name = `${(a.label || a.key).replace(/[\\/:*?"<>|]/g, " ").trim() || "素材"}.${ext}`;
      let f = new File([blob], name, { type });
      if (type.startsWith("image/")) f = await sendablePicture(f, name).catch(() => f);
      await pickFile(f, true);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "这个素材取不到，再按一次");
    } finally {
      setLibBusy("");
    }
  }

  // A clip belongs to the words that produced it. Editing the text without
  // clearing it leaves a player that sounds like the previous draft, and the
  // agent hears something the customer will not receive.
  function edit(v: string) {
    setText(v);
    setErr(null);
    dropClip();
  }

  function dropClip() {
    ttsSeq.current++;
    setMaking(false);
    if (clip) {
      URL.revokeObjectURL(clip);
      setClip(null);
    }
  }

  function insert(e: string) {
    const el = boxRef.current;
    if (!el) {
      edit(text + e);
      return;
    }
    const at = el.selectionStart ?? text.length;
    edit(text.slice(0, at) + e + text.slice(el.selectionEnd ?? at));
    queueMicrotask(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = at + e.length;
    });
  }

  // WhatsApp's own ceiling for a note that still plays inline is about
  // half a megabyte; two minutes of Opus is comfortably inside it, and a
  // hard stop is kinder than a refusal after the fact.
  const REC_MAX_SECS = 120;

  // The first type this browser will actually record. Chrome gives WebM,
  // Firefox can give Ogg, Safari MP4 — the server takes all three.
  function recorderType(): string {
    const wanted = ["audio/ogg;codecs=opus", "audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
    for (const t of wanted) {
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(t)) return t;
    }
    return "";
  }

  function stopTicking() {
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    stopMeter();
  }

  // The bars of the voice coming in. Without an audio context the time still runs, with no bars.
  function startMeter(stream: MediaStream) {
    stopMeter();
    setLevels({ n: 0, bars: [] });
    try {
      const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const an = ctx.createAnalyser();
      an.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(an);
      const buf = new Uint8Array(an.fftSize);
      const timer = window.setInterval(() => {
        an.getByteTimeDomainData(buf);
        const h = level(buf);
        setLevels((l) => ({ n: l.n + 1, bars: [...l.bars, h].slice(-REC_BARS * 3) }));
      }, 250);
      meterRef.current = { ctx, timer };
    } catch {
      // No meter on this browser.
    }
  }

  function stopMeter() {
    const m = meterRef.current;
    if (!m) return;
    window.clearInterval(m.timer);
    void m.ctx.close().catch(() => undefined);
    meterRef.current = null;
  }

  // Releases the microphone. Left running, the browser keeps the recording
  // indicator lit and the agent thinks they are still being heard.
  function releaseMic() {
    recRef.current?.stream.getTracks().forEach((t) => t.stop());
    recRef.current = null;
  }

  async function startRecording() {
    setErr(null);
    setPop("");
    dropClip();
    const type = recorderType();
    if (!type || !navigator.mediaDevices?.getUserMedia) {
      setErr("这个浏览器不能录音，用 Chrome 试试");
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setErr("浏览器不让用麦克风 —— 在网址左边的锁那里允许一次");
      return;
    }
    const mr = new MediaRecorder(stream, { mimeType: type });
    chunksRef.current = [];
    sendOnStop.current = false;
    binned.current = false;
    mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
    mr.onstop = () => {
      stopTicking();
      releaseMic();
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];
      if (blob.size === 0 || binned.current) {
        binned.current = false;
        setRec("off");
        return;
      }
      const next = { blob, url: URL.createObjectURL(blob) };
      setTake((old) => {
        if (old) URL.revokeObjectURL(old.url);
        return next;
      });
      setRec("ready");
      if (sendOnStop.current) {
        sendOnStop.current = false;
        void sendRecording(next);
      }
    };
    recRef.current = mr;
    mr.start();
    startMeter(stream);
    setSecs(0);
    setRec("on");
    tickRef.current = window.setInterval(() => {
      setSecs((s) => {
        if (s + 1 >= REC_MAX_SECS) {
          // Stop ourselves rather than let the send fail on length.
          recRef.current?.stop();
          return REC_MAX_SECS;
        }
        return s + 1;
      });
    }, 1000);
  }

  function stopRecording() {
    recRef.current?.stop();
    stopTicking();
  }

  // The bin: nothing is kept and nothing was sent.
  function discardRecording() {
    sendOnStop.current = false;
    if (recRef.current) binned.current = true;
    recRef.current?.stop();
    stopTicking();
    releaseMic();
    chunksRef.current = [];
    setTake((old) => {
      if (old) URL.revokeObjectURL(old.url);
      return null;
    });
    setSecs(0);
    setRec("off");
  }

  // 重录: the take goes, and a new one starts at once.
  function redoRecording() {
    discardRecording();
    void startRecording();
  }

  async function sendRecording(t = take) {
    if (!t) return;
    setBusy("voice");
    setErr(null);
    try {
      const ext = t.blob.type.includes("ogg") ? "ogg" : t.blob.type.includes("mp4") ? "m4a" : "webm";
      await api.sendRecording(waID, t.blob, `recording.${ext}`);
      URL.revokeObjectURL(t.url);
      setTake(null);
      setSecs(0);
      setRec("off");
      followNext(waID);
      onSent();
    } catch (e) {
      failed(e, "语音发不出去");
    } finally {
      setBusy("");
    }
  }

  // The round button while recording: stop and send in one press, as WhatsApp's.
  function sendWhileRecording() {
    if (rec === "ready") {
      void sendRecording();
      return;
    }
    sendOnStop.current = true;
    stopRecording();
  }

  function failed(e: unknown, fallback: string) {
    setErr(e instanceof ApiError ? e.message : fallback);
  }

  async function preview() {
    const mine = ++ttsSeq.current;
    setMaking(true);
    setErr(null);
    try {
      const blob = await api.previewVoice(text);
      if (mine !== ttsSeq.current) return; // the words changed, or ✕
      setClip(URL.createObjectURL(blob));
    } catch (e) {
      if (mine === ttsSeq.current) failed(e, "转不成语音，再按一次");
    } finally {
      if (mine === ttsSeq.current) setMaking(false);
    }
  }

  async function send() {
    setBusy("text");
    setErr(null);
    try {
      if (attach) await api.sendMedia(waID, attach, text.trim(), quoting);
      else await api.sendText(waID, text, quoting);
      pickAttach(null);
      edit("");
      onClearReply?.();
      followNext(waID);
      onSent();
    } catch (e) {
      failed(e, "发不出去");
    } finally {
      setBusy("");
    }
  }

  async function sendVoice() {
    setBusy("voice");
    setErr(null);
    try {
      await api.sendVoice(waID, text);
      edit("");
      followNext(waID);
      onSent();
    } catch (e) {
      failed(e, "语音发不出去");
    } finally {
      setBusy("");
    }
  }

  // A picture on the clipboard becomes the attachment; text pastes as text.
  // Copying a picture in Chrome also puts its <img> on the clipboard as HTML,
  // so the file is looked for first.
  async function onPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    if (!canText || working) return;
    const item = [...e.clipboardData.items].find((i) => i.kind === "file" && i.type.startsWith("image/"));
    const f = item?.getAsFile();
    if (!f) return;
    e.preventDefault();
    try {
      pickAttach(await pastedPicture(f));
    } catch {
      setErr("这张图贴不上：存到电脑，再按回形针选");
    }
  }

  // Enter sends, Shift+Enter breaks the line — at a keyboard. On a phone the
  // keyboard's Enter is a new line and the button sends, as in the app: an
  // Enter meant for a new line sent half a message.
  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Esc lets the quoted line go, as WhatsApp's does.
    if (e.key === "Escape" && replyTo) {
      e.preventDefault();
      onClearReply?.();
      return;
    }
    if (e.key === "Enter" && !e.shiftKey && !imeComposing(e) && !touch()) {
      e.preventDefault();
      if (canSendText) void send();
    }
  }

  // The take's bars, drawn from what the meter heard, for the player that waits with it.
  const takeBars = useMemo(() => {
    const b = levels.bars;
    if (!b.length) return undefined;
    return Array.from({ length: 36 }, (_, i) => {
      const from = Math.floor((i * b.length) / 36);
      const s = b.slice(from, Math.max(from + 1, Math.floor(((i + 1) * b.length) / 36)));
      return s.length ? Math.max(...s) : 20;
    });
  }, [levels]);

  if (!canText && !canVoice) return null;

  // What the round button does with an empty box: the mic for whoever may
  // speak; otherwise the send, waiting.
  const mic = canVoice && empty && !attach && !fitting;
  const speaking = making || !!clip;
  const shown = levels.bars.slice(-REC_BARS);

  return (
    <div className="wc-foot" ref={footRef}>
      {/* Who is answering, and that a send here stops them (prototype v2.20 .chat-aihint); not while recording. */}
      {botName && rec === "off" && (
        <div className="wc-aihint">
          <ShellIcon name="sparkles" size={13} />
          {botName}在回这个客户 · 你一发，{botName}就先停
        </div>
      )}
      {err && (
        <div className="wc-err" role="alert">
          <ShellIcon name="alert" size={14} />
          {err}
        </div>
      )}
      {fitting && (
        <div className="wc-att wc-strip">
          <span className="wc-attp-th">
            <i className="wc-spin" aria-hidden="true" />
          </span>
          <span className="wc-attp-t">
            <b>{fitLine}</b>
            <small>压好就可以发，影片越长越久</small>
          </span>
          <button type="button" className="wc-pico sm" title="不压了" aria-label="不压了" onClick={stopFit}>
            <ShellIcon name="x" size={16} />
          </button>
        </div>
      )}
      {libBusy && !attach && !fitting && (
        <div className="wc-att wc-strip">
          <span className="wc-attp-th">
            <i className="wc-spin" aria-hidden="true" />
          </span>
          <span className="wc-attp-t">
            <b>{lib?.assets.find((a) => a.key === libBusy)?.label || "素材"}</b>
            <small>素材库 · 正在取…</small>
          </span>
        </div>
      )}
      {attach && attachURL && (
        <div className="wc-att wc-strip">
          <span className="wc-attp-th">
            {attach.type.startsWith("video/") ? <video src={attachURL} muted preload="metadata" /> : <img src={attachURL} alt="" />}
            {attach.type.startsWith("video/") && (
              <b>
                <ShellIcon name="play" size={12} />
              </b>
            )}
          </span>
          <span className="wc-attp-t">
            <b>{attach.name}</b>
            <small>
              {fitNote || (attachLib ? "素材库" : sizeText(attach.size))} · {attach.type.startsWith("video/") ? "影片" : "图片"} · 先看一眼，再按发送
            </small>
          </span>
          <button type="button" className="wc-pico sm" title="不发了" aria-label="不发了" onClick={() => pickAttach(null)}>
            <ShellIcon name="x" size={16} />
          </button>
        </div>
      )}
      {/* 转语音: the clip of the typed words, heard before it is sent. */}
      {speaking && rec === "off" && (
        <div className="wc-tts wc-strip">
          <span className="wc-strip-i" aria-hidden="true">
            <Glyph name="speak" size={16} />
          </span>
          {clip ? (
            <ClipPlayer src={clip} autoPlay />
          ) : (
            <span className="wc-tts-wait">
              <i className="wc-spin" aria-hidden="true" />
              正在转成语音…
            </span>
          )}
          <button
            type="button"
            className="wc-pico sm"
            title="不发语音"
            aria-label="不发语音"
            onPointerDown={keepFocus}
            onClick={dropClip}
            disabled={busy === "voice"}
          >
            <ShellIcon name="x" size={16} />
          </button>
          <button type="button" className="wc-tts-send" onPointerDown={keepFocus} onClick={() => void sendVoice()} disabled={!clip || busy !== ""}>
            <ShellIcon name="send" size={16} />
            {busy === "voice" ? "发送中…" : "发语音"}
          </button>
        </div>
      )}
      {/* 回复: the line this message answers, over the box as in WhatsApp. A
          voice note goes without it: WhatsApp's voice notes from here quote nothing. */}
      {replyTo && rec === "off" && (
        <div className="wc-replybar wc-strip">
          <span className="wc-replybar-q">
            <b>回复 {quoteWho(replyTo, peerName)}</b>
            <small>{quoteText(replyTo)}</small>
          </span>
          <button type="button" className="wc-pico sm" title="不回这句" aria-label="不回这句" onPointerDown={keepFocus} onClick={onClearReply}>
            <ShellIcon name="x" size={16} />
          </button>
        </div>
      )}
      <div className="wc-foot-r">
        {rec !== "off" ? (
          // While recording — and while a take waits — the box is the
          // recording bar, as WhatsApp's is: bin, the time, the voice coming
          // in, stop; then 取消, the take to listen to, 重录.
          <div className={`wc-box wc-rec${rec === "ready" ? " ready" : ""}`}>
            {rec === "on" ? (
              <>
                <button type="button" className="wc-pico wc-bin" title="不要了" aria-label="不要了" onClick={discardRecording} disabled={busy === "voice"}>
                  <ShellIcon name="trash" />
                </button>
                <span className="wc-rec-dot" aria-hidden="true" />
                <b className="wc-rec-t">{fmtSecs(secs)}</b>
                <span className="wc-rec-w" aria-hidden="true">
                  {shown.map((h, i) => (
                    <i key={levels.n - shown.length + i} style={{ height: `${h}%` }} />
                  ))}
                </span>
                <span className="wc-rec-hint">正在录音…</span>
                <button type="button" className="wc-pico wc-rec-stop" title="停，先听一遍" aria-label="停，先听一遍" onClick={stopRecording}>
                  <Glyph name="stop" />
                </button>
              </>
            ) : (
              <>
                <button type="button" className="wc-recbtn" title="不要了" aria-label="取消，不发" onClick={discardRecording} disabled={busy === "voice"}>
                  <ShellIcon name="trash" size={16} />
                  <span>取消</span>
                </button>
                {take && <ClipPlayer src={take.url} hint={secs} bars={takeBars} />}
                <button type="button" className="wc-recbtn" title="重录" aria-label="重录" onClick={redoRecording} disabled={busy === "voice"}>
                  <Glyph name="redo" size={16} />
                  <span>重录</span>
                </button>
              </>
            )}
          </div>
        ) : (
          // .typing: on a phone the paperclip gives its place to 转语音 once there
          // are words, as WhatsApp's camera gives way — and the words keep the
          // width. 转语音 as a pill of its own beside the box squeezed them to
          // twelve characters a line (owner, 2026-10-04: 「很丑…你还挤得那么小」).
          <div className={`wc-box${empty ? "" : " typing"}`}>
            {canText && (
              <button
                type="button"
                className={`wc-pico wc-emo${pop === "emoji" ? " on" : ""}`}
                title="表情"
                aria-label="表情"
                aria-expanded={pop === "emoji"}
                onClick={() => setPop(pop === "emoji" ? "" : "emoji")}
              >
                <Glyph name="smile" />
              </button>
            )}
            <textarea
              ref={boxRef}
              rows={1}
              value={text}
              onChange={(e) => edit(e.target.value)}
              onKeyDown={onKeyDown}
              onPaste={(e) => void onPaste(e)}
              placeholder={attach ? "加一句说明（可不填）" : "输入消息"}
              aria-label="输入消息"
              enterKeyHint={touch() ? "enter" : "send"}
              // Read-only, not disabled, while a send is on its way: a disabled
              // box loses the focus, and the phone's keyboard goes with it.
              readOnly={working}
            />
            {counter && <span className={`wc-count${overText || (canVoice && overVoice) ? " over" : ""}`}>{counter}</span>}
            {canText && (
              <>
                <button
                  type="button"
                  className={`wc-pico wc-clipbtn${pop === "attach" ? " on" : ""}`}
                  title="发图片、影片"
                  aria-label="发图片、影片"
                  aria-expanded={pop === "attach"}
                  onClick={() => setPop(pop === "attach" ? "" : "attach")}
                  disabled={working || fitting}
                >
                  <ShellIcon name="clip" />
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,video/*"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    e.target.value = "";
                    if (f) void pickFile(f);
                  }}
                />
              </>
            )}
            {/* 转语音, once there are words to speak: in the box, at its end. */}
            {canVoice && !empty && !attach && !speaking && (
              <button
                type="button"
                className="wc-speak"
                onPointerDown={keepFocus}
                onClick={() => void preview()}
                disabled={!canSpeak}
                title={overVoice ? `太长了，念出来最多 ${VOICE_MAX} 个字` : "转语音：用克隆的声音念出来，先听再发"}
                aria-label="转语音"
              >
                <Glyph name="speak" size={15} />
                <span>转语音</span>
              </button>
            )}
          </div>
        )}
        {rec !== "off" ? (
          <button
            type="button"
            className={`wc-send${rec === "on" ? " rec" : ""}`}
            onClick={sendWhileRecording}
            disabled={working}
            title={rec === "on" ? "停下，马上发" : "发送"}
            aria-label="发出去"
          >
            <ShellIcon name="send" size={20} />
          </button>
        ) : mic ? (
          <button
            type="button"
            className="wc-send"
            onClick={() => void startRecording()}
            disabled={working}
            title="按一下开始录音，用你自己的声音"
            aria-label="录音"
          >
            <ShellIcon name="mic" size={20} />
          </button>
        ) : (
          canText && (
            <button
              type="button"
              className="wc-send"
              onPointerDown={keepFocus}
              onClick={() => void send()}
              disabled={!canSendText}
              title="发送"
              aria-label="发送"
            >
              {busy === "text" ? <i className="wc-spin" aria-hidden="true" /> : <ShellIcon name="send" size={20} />}
            </button>
          )
        )}
      </div>
      {/* The cards over the box: they lie over the chat, so nothing in it moves (QA G2-19). */}
      {pop === "emoji" && (
        <div className="wc-pop wc-emojis" role="dialog" aria-label="表情">
          {EMOJI.map((e) => (
            <button type="button" key={e} onPointerDown={keepFocus} onClick={() => insert(e)}>
              {e}
            </button>
          ))}
        </div>
      )}
      {pop === "attach" && (
        <div className="wc-pop wc-attpop" role="dialog" aria-label="发图片、影片">
          <button type="button" className="wc-att-pick" onClick={() => fileRef.current?.click()}>
            <span className="wc-att-pi">
              <Glyph name="photos" size={18} />
            </span>
            <span>
              <b>照片和影片</b>
              <small>从电脑或手机选 · 也可以直接贴上</small>
            </span>
            <ShellIcon name="right" size={15} />
          </button>
          {!lib && !libRead && (
            <div className="wc-att-wait">
              <i className="wc-spin" aria-hidden="true" />
              素材库读取中…
            </div>
          )}
          {lib && (
            <>
              <div className="wc-pop-t">
                <span>素材库 · 按一下先看再发</span>
                <small>{libQ.trim() ? `${libShown.length} / ${lib.assets.length}` : `${lib.assets.length} 个`}</small>
              </div>
              {/* Every file the library may still send, found by its name or its rule; Enter takes the first. */}
              <label className="wc-att-q">
                <ShellIcon name="search" size={15} />
                <input
                  value={libQ}
                  onChange={(e) => searchLib(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !imeComposing(e) && libShown[0]) {
                      e.preventDefault();
                      void pickLibrary(libShown[0]);
                    }
                  }}
                  placeholder="搜素材：名字或什么时候发"
                  aria-label="搜素材库"
                  autoFocus={!touch()}
                />
                {libQ && (
                  <button type="button" className="wc-pico sm" title="清除" aria-label="清除" onClick={() => searchLib("")}>
                    <ShellIcon name="x" size={14} />
                  </button>
                )}
              </label>
              {libShown.length ? (
                <div className="wc-att-g" ref={libGrid}>
                  {libShown.map((a) => (
                    <LibTile key={a.key} id={lib.id} asset={a} busy={!!libBusy} onPick={() => void pickLibrary(a)} />
                  ))}
                </div>
              ) : (
                <div className="wc-att-none">没有对上的素材</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** A clip's bars when nothing measured them: steady per length, so a re-render never reshuffles them. */
function clipBars(seed: number, n = 32): number[] {
  return Array.from({ length: n }, (_, i) => Math.round(24 + 76 * Math.abs(Math.sin(seed * 1.31 + i * 1.7) * 0.6 + Math.sin(i * 0.47 + seed) * 0.4)));
}

/**
 * A clip waiting to be sent — the typed words spoken, or a recording — as a
 * small voice-note player (prototype v2.20 ChatClip): play, the bars filling as
 * it goes, how long. `hint` is the length when the file does not say (Chrome's
 * recordings do not); `bars` what the meter heard while recording.
 */
function ClipPlayer({ src, autoPlay, hint = 0, bars }: { src: string; autoPlay?: boolean; hint?: number; bars?: number[] }) {
  const ref = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [meta, setMeta] = useState(0);
  const [error, setError] = useState("");
  const dur = meta > 0 ? meta : hint;
  const shape = useMemo(() => bars ?? clipBars(Math.round(dur) * 7 + 3), [bars, dur]);

  // Pressed 转语音 to hear it: it plays as it arrives. A browser that wants a
  // second press for sound (the wait took too long) still shows the ▶.
  useEffect(() => {
    if (!autoPlay) return;
    ref.current?.play().catch(() => undefined);
  }, [src, autoPlay]);

  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (!a.paused) {
      a.pause();
      return;
    }
    setError("");
    a.play().catch((e: unknown) => {
      if (e instanceof DOMException && e.name === "AbortError") return;
      setError(a.canPlayType("audio/ogg; codecs=opus") === "" ? "这台设备放不了这种格式" : "放不出来");
    });
  };
  const frac = dur > 0 ? Math.min(1, pos / dur) : 0;
  return (
    <span className={`wc-clipp${playing ? " playing" : ""}`}>
      <audio
        ref={ref}
        src={src}
        preload="auto"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setPos(0);
        }}
        onTimeUpdate={(e) => setPos(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => Number.isFinite(e.currentTarget.duration) && setMeta(e.currentTarget.duration)}
        onDurationChange={(e) => Number.isFinite(e.currentTarget.duration) && setMeta(e.currentTarget.duration)}
        onError={() => setError("放不出来")}
      />
      <button type="button" className="wc-clipp-btn" onPointerDown={keepFocus} onClick={toggle} aria-label={playing ? "暂停" : "先听一遍"} title={playing ? "暂停" : "先听一遍"}>
        <ShellIcon name={playing ? "pause" : "play"} size={14} />
      </button>
      {error ? (
        <span className="wc-clipp-err">{error}</span>
      ) : (
        <span className="wc-clipp-w" aria-hidden="true">
          {shape.map((h, i) => (
            <i key={i} className={(i + 0.5) / shape.length <= frac ? "on" : ""} style={{ height: `${h}%` }} />
          ))}
        </span>
      )}
      <span className="wc-clipp-t">{fmtSecs(playing || pos > 0 ? pos : dur)}</span>
    </span>
  );
}

/** A library picture as one the chat can send, under its own name (the pasted-picture rules below). */
async function sendablePicture(f: File, name: string): Promise<File> {
  if (SENDABLE.has(f.type) && f.size <= PASTE_MAX_BYTES) return f;
  const out = await pastedPicture(f);
  return new File([out], name.replace(/\.[^.]+$/, ".jpg"), { type: out.type });
}

/** What WhatsApp takes as a picture and the API passes on (console/media_send.go). */
const SENDABLE = new Set(["image/jpeg", "image/png", "image/webp"]);
/** Under the API's 5 MB for a picture, with room for the multipart around it. */
const PASTE_MAX_BYTES = 4.5 * 1024 * 1024;
/** The longest side a re-drawn picture keeps: a phone shows no more. */
const PASTE_MAX_SIDE = 2560;

/**
 * A pasted picture as one the chat can send: as it is when WhatsApp takes it
 * and it is small enough, otherwise drawn again as a JPEG — a GIF or BMP off a
 * web page, or a screenshot of a whole screen as an 8 MB PNG.
 */
async function pastedPicture(f: File): Promise<File> {
  const ext = f.type.split("/")[1] === "jpeg" ? "jpg" : f.type.split("/")[1];
  if (SENDABLE.has(f.type) && f.size <= PASTE_MAX_BYTES) return new File([f], `贴上的图片.${ext}`, { type: f.type });
  const img = await createImageBitmap(f);
  const scale = Math.min(1, PASTE_MAX_SIDE / Math.max(img.width, img.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  const g = canvas.getContext("2d");
  if (!g) throw new Error("no canvas");
  // A transparent PNG would turn black as a JPEG: white under it, as WhatsApp shows it.
  g.fillStyle = "#fff";
  g.fillRect(0, 0, canvas.width, canvas.height);
  g.drawImage(img, 0, 0, canvas.width, canvas.height);
  img.close();
  for (const quality of [0.9, 0.8, 0.65]) {
    const blob = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, "image/jpeg", quality));
    if (blob && blob.size <= PASTE_MAX_BYTES) return new File([blob], "贴上的图片.jpg", { type: "image/jpeg" });
  }
  throw new Error("too big");
}
